import { useReducer, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Search, Store, ChevronLeft } from 'lucide-react'
import type { Product, DiningTable, OrderItem, StationKOT, Category, APIResponse } from '@/types'
import { KOTSidebar } from './KOTSidebar'
import { VOIDABLE_STATUSES } from '@/components/counter/rail/SentItemsSection'
import { PinEntryModal } from './PinEntryModal'
import { subscribeOrderReady } from '@/lib/kdsRealtime'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { invalidateTables, isTableAffectingEvent } from '@/lib/tablesCache'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'
import { isAndroidPrintClient } from '@/lib/bhooklyAppPrint'
import { shouldRelayCounterPrint, watchRelayedPrintJobs } from '@/lib/counterRelay'
import { KotPrintModal } from '@/components/counter/KotPrintModal'
import { toastHelpers } from '@/lib/toast-helpers'
import { TableFloorMap } from '@/components/tables/TableFloorMap'
import { useReservedTableGuard } from '@/components/reservations/ReservedTableGuard'
import { holdLabel } from '@/lib/reservations'
import { ServerTableActionRail } from './ServerTableActionRail'
import { isKotUnsentStatus } from './kotConstants'
import { useCurrency } from '@/contexts/CurrencyContext'
import { buildFloorTabs } from '@/lib/managedFloors'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { parseFloorLayouts, resolveFloorDims } from '@/lib/tableMapSizing'
import { ProductImagePlaceholder } from '@/components/menu/ProductImagePlaceholder'
import { ModifierConfigurator, type ConfiguredLine } from '@/components/menu/ModifierConfigurator'
import { useMenuModifiers } from '@/lib/modifierCatalogue'
import { resolveLineUnitPrice, type LineSelection } from '@/lib/lineModifiers'
import { buildMenuRulesIndex, markSoldOutDealChoiceMap, markSoldOutDealChoices, useMenuClock, useMenuRules } from '@/lib/menuRulesQuery'
import { selectionSignature, selectionsFromOrderItem } from './kotLineOptions'
import { getDraftUserId } from '@/components/counter/useCounterDraft'
import {
  clearTicket,
  readTicket,
  restoreTicketLines,
  toPersistedLines,
  useTicketAutosave,
  type TicketFields,
} from './useServerTicketDraft'
import { useOfflineCapture } from '@/lib/offline/useOfflineCapture'
import { useOnlineAndReachable } from '@/lib/offline/reachability'
import { listOfflineOrders, type OfflineOrder } from '@/lib/offline/offlineStore'
import { offlineHintForRefusal } from '@/lib/offline/refusalCopy'
import { offlineServerName } from '@/lib/offline/serverName'
import { OfflineOrdersTray } from '@/components/counter/OfflineOrdersTray'
import { withoutRetiredProducts } from '@/lib/productVisibility'
import { heldEntryForTable, heldEntryToKotItems, mergeHeldTables } from './floorOffline'
import { MenuCategoryBar } from '@/components/menu/MenuCategoryBar'
import { categoryColor, categorySurfaceStyle } from '@/lib/menuCategoryStyle'

type ItemStatus = 'draft' | 'pending' | 'sent' | 'preparing' | 'ready' | 'served' | 'voided'

export interface KOTItem {
  id: string
  product_id: string
  product_name: string
  quantity: number
  unit_price: number
  special_instructions: string
  status: ItemStatus
  category_id?: string
  /** Course this line belongs to (1=first). Held later courses fire on demand. */
  course_number?: number
  /**
   * Chosen modifier options for this line (F-MOD-N7 on the floor).
   *
   * Not cosmetic: the two endpoints this screen posts to — order create and
   * add-items — both resolve modifiers in STRICT mode, so a dish with a
   * required group is REFUSED outright (`modifier_required_group_missing`)
   * rather than rung at the wrong price. Before this existed, a waiter simply
   * could not ring any dish carrying a size or a protein choice.
   */
  selections: LineSelection[]
}

interface KOTState {
  orderId: string | null
  tableId: string | null
  tableName: string | null
  guestCount: number
  items: KOTItem[]
  phase: 'table_select' | 'guest_count' | 'ordering'
  pinModal: { open: boolean; itemId: string | null; itemName: string; qty: number; price: number }
}

type KOTAction =
  | { type: 'SELECT_TABLE'; tableId: string; tableName: string }
  | { type: 'SET_GUEST_COUNT'; guestCount: number }
  | { type: 'ADD_ITEM'; product: Product; configured?: ConfiguredLine | null; unitPrice?: number }
  | {
      type: 'CONFIGURE_LINE'
      itemId: string
      selections: LineSelection[]
      quantity: number
      unitPrice: number
    }
  | { type: 'UPDATE_QTY'; itemId: string; qty: number }
  | { type: 'SET_ITEM_NOTES'; itemId: string; note: string }
  | { type: 'REMOVE_DRAFT'; itemId: string }
  | { type: 'MARK_SENT'; itemIds: string[] }
  | { type: 'REQUEST_VOID'; itemId: string; itemName: string; qty: number; price: number }
  | { type: 'CONFIRM_VOID'; itemId: string }
  | { type: 'CLOSE_PIN_MODAL' }
  | { type: 'ORDER_CREATED'; orderId: string; items: KOTItem[] }
  | { type: 'SYNC_ITEMS'; items: KOTItem[] }
  | {
      type: 'RESTORE_TICKET'
      orderId: string | null
      tableId: string | null
      tableName: string | null
      guestCount: number
      items: KOTItem[]
    }
  | { type: 'RESET' }

const initialState: KOTState = {
  orderId: null,
  tableId: null,
  tableName: null,
  guestCount: 0,
  items: [],
  phase: 'table_select',
  pinModal: { open: false, itemId: null, itemName: '', qty: 0, price: 0 },
}

function kotReducer(state: KOTState, action: KOTAction): KOTState {
  switch (action.type) {
    case 'SELECT_TABLE':
      return { ...state, tableId: action.tableId, tableName: action.tableName, phase: 'guest_count' }
    case 'SET_GUEST_COUNT':
      return { ...state, guestCount: action.guestCount, phase: 'ordering' }
    case 'ADD_ITEM': {
      const selections = action.configured?.selections ?? []
      const addQty = action.configured?.quantity ?? 1
      const sig = selectionSignature(selections)
      // Only fold into a line whose OPTIONS match too. A Large and a Regular of
      // the same dish are different food at different prices; merging them on
      // product id alone would silently drop one of the guest's choices.
      const existing = state.items.find(
        i =>
          i.product_id === action.product.id &&
          isKotUnsentStatus(i.status) &&
          selectionSignature(i.selections) === sig
      )
      if (existing) {
        return {
          ...state,
          items: state.items.map(i =>
            i.id === existing.id ? { ...i, quantity: i.quantity + addQty } : i
          ),
        }
      }
      const newItem: KOTItem = {
        id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        product_id: action.product.id,
        product_name: action.product.name,
        quantity: addQty,
        unit_price: action.unitPrice ?? action.product.price,
        special_instructions: '',
        status: 'draft',
        category_id: action.product.category_id,
        course_number: 1,
        selections,
      }
      return { ...state, items: [...state.items, newItem] }
    }
    case 'CONFIGURE_LINE': {
      const edited = state.items.map(i =>
        i.id === action.itemId
          ? { ...i, selections: action.selections, quantity: action.quantity, unit_price: action.unitPrice }
          : i
      )
      // Editing one line into an exact match of another must not leave two
      // identical rows on the ticket — fold them, same as the counter does.
      const target = edited.find(i => i.id === action.itemId)
      if (!target) return { ...state, items: edited }
      const sig = selectionSignature(target.selections)
      const twin = edited.find(
        i =>
          i.id !== target.id &&
          i.product_id === target.product_id &&
          isKotUnsentStatus(i.status) &&
          selectionSignature(i.selections) === sig
      )
      if (!twin) return { ...state, items: edited }
      return {
        ...state,
        items: edited
          .filter(i => i.id !== target.id)
          .map(i => (i.id === twin.id ? { ...i, quantity: i.quantity + target.quantity } : i)),
      }
    }
    case 'UPDATE_QTY':
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.itemId ? { ...i, quantity: Math.max(1, action.qty) } : i
        ),
      }
    case 'SET_ITEM_NOTES':
      return {
        ...state,
        items: state.items.map((i) =>
          i.id === action.itemId && isKotUnsentStatus(i.status)
            ? { ...i, special_instructions: action.note }
            : i,
        ),
      }
    case 'REMOVE_DRAFT':
      return { ...state, items: state.items.filter(i => i.id !== action.itemId) }
    case 'MARK_SENT':
      return {
        ...state,
        items: state.items.map(i =>
          action.itemIds.includes(i.id) ? { ...i, status: 'sent' as ItemStatus } : i
        ),
      }
    case 'REQUEST_VOID':
      return {
        ...state,
        pinModal: { open: true, itemId: action.itemId, itemName: action.itemName, qty: action.qty, price: action.price },
      }
    case 'CONFIRM_VOID':
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.itemId ? { ...i, status: 'voided' as ItemStatus } : i
        ),
        pinModal: { open: false, itemId: null, itemName: '', qty: 0, price: 0 },
      }
    case 'CLOSE_PIN_MODAL':
      return { ...state, pinModal: { open: false, itemId: null, itemName: '', qty: 0, price: 0 } }
    case 'ORDER_CREATED':
      return { ...state, orderId: action.orderId, items: action.items }
    case 'SYNC_ITEMS':
      return { ...state, items: action.items }
    // F-FE-N109 — put a persisted ticket back in ONE dispatch. Replaying
    // SELECT_TABLE -> SET_GUEST_COUNT -> n x ADD_ITEM would work, but it
    // walks the ticket through phases the server never saw and re-folds
    // lines that were already folded when they were staged.
    case 'RESTORE_TICKET':
      // Only ever into an untouched slate. The restore of a ticket that was
      // already fired has to await getOrder first, and in that window the
      // server can tap a table — landing here afterwards would drop them onto
      // somebody else's ticket. Guarding in the reducer rather than at the call
      // site is what makes that race impossible rather than unlikely.
      if (state.phase !== 'table_select' || state.items.length > 0) return state
      return {
        ...state,
        orderId: action.orderId,
        tableId: action.tableId,
        tableName: action.tableName,
        guestCount: action.guestCount,
        items: action.items,
        phase: 'ordering',
      }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

// Map a persisted order's items into the local KOT ticket shape.
function mapOrderItemsToKot(items: OrderItem[] | undefined): KOTItem[] {
  return (items || []).map((oi: OrderItem) => ({
    id: oi.id,
    product_id: oi.product_id,
    product_name: oi.product?.name || '',
    quantity: oi.quantity,
    unit_price: oi.unit_price,
    special_instructions: oi.special_instructions || '',
    status: oi.status as ItemStatus,
    category_id: oi.product?.category_id,
    course_number: oi.course_number ?? 1,
    selections: selectionsFromOrderItem(oi),
  }))
}

export function KOTServerInterface() {
  const [state, dispatch] = useReducer(kotReducer, initialState)
  // F-RSV-N1: tables held for an upcoming booking show "Reserved …" and ask
  // before a walk-in is seated at them.
  const reservedGuard = useReservedTableGuard()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [guestInput, setGuestInput] = useState('')
  const [fireBusy, setFireBusy] = useState(false)
  // The occupied table whose action rail is open in the permanent right rail.
  // Selecting an occupied tile opens this rail instead of jumping straight
  // into the ordering workspace.
  const [selectedFloorTableId, setSelectedFloorTableId] = useState<string | null>(null)
  const [layoutLocationFilter, setLayoutLocationFilter] = useState<string>('Main Floor')
  // Shared 1s clock driving the live seated timers on tiles + the action rail.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<'menu' | 'ticket'>(
    'menu',
  )
  const [kotPrintOpen, setKotPrintOpen] = useState(false)
  const [voidOrderOpen, setVoidOrderOpen] = useState(false)
  const [lastFireKots, setLastFireKots] = useState<StationKOT[] | undefined>(undefined)
  const [orderNotes, setOrderNotes] = useState('')
  const [lastTappedProductId, setLastTappedProductId] = useState<string | null>(null)
  const [tapPop, setTapPop] = useState<{ productId: string; nonce: number }>({
    productId: '',
    nonce: 0,
  })
  const tapFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Promoted from 1024px → 1280px so square / awkward monitors (1024×1024,
  // 1280×1024) get the cleaner tabbed menu/ticket UI instead of a crushed
  // side-by-side layout.
  const isLgUp = useMediaQuery('(min-width: 1280px)')
  // Portrait tablets: floor layouts are authored wide in the admin builder, so
  // a side rail leaves the map a narrow tall strip at a tiny fit zoom. Stack
  // instead — map on top at full width, table details as a bottom panel.
  // Portrait phones (<768px) keep the slide-in drawer.
  const stackedPortrait = useMediaQuery('(orientation: portrait) and (min-width: 768px)')
  const queryClient = useQueryClient()
  const { formatCurrency } = useCurrency()

  // F-MOD-N7 on the floor. Shares the counter's catalogue query and its
  // "prompt only when something is required" rule, so a dish behaves the same
  // whether it is rung at the till or at the table.
  const { data: menuModifiers, isLoading: menuModifiersLoading } = useMenuModifiers()
  // F-DEAL-N1/N2: dine-in prices by the clock (happy hour), item hours and
  // deal parts — the same rules the counter and the server apply.
  const menuRules = useMenuRules()
  const menuClock = useMenuClock(menuRules.utc_offset_minutes)
  const rulesIndex = useMemo(() => buildMenuRulesIndex(menuRules, menuClock), [menuRules, menuClock])
  const dineInPrice = useCallback((p: Product) => rulesIndex.basePrice(p, 'dine_in').price, [rulesIndex])

  // ── Offline capture (F-FE-N125) ──────────────────────────────────────────
  //
  // The same engine the counter runs (F-FE-N30 … N124). The floor never had
  // it: Fire went to the API and toasted "Fire KOT failed" when the link was
  // down, on the device most likely to be in someone's hand when it dropped.
  // Reachability comes from the root probe, so this screen inherits an
  // accurate signal without polling anything itself.
  const { online: osOnline, backendReachable } = useOnlineAndReachable()
  const online = osOnline && backendReachable
  const offlineCapture = useOfflineCapture({ online })
  /** Pending CREATE entries with a table — the ones the floor map overlays. */
  const [pendingHeld, setPendingHeld] = useState<OfflineOrder[]>([])
  const [pendingHeldLoaded, setPendingHeldLoaded] = useState(false)
  /** The held entry the open ticket belongs to (the floor's `offlineTarget`). */
  const [heldOffline, setHeldOffline] = useState<OfflineOrder | null>(null)
  /**
   * A server-side order opened while offline: the rail cannot load it, so the
   * ticket carries only the cached summary and new lines append on replay.
   */
  const [offlineAppend, setOfflineAppend] = useState<{
    orderNumber: string
    itemCount: number | null
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const all = await listOfflineOrders()
      if (cancelled) return
      setPendingHeld(
        all.filter((o) => o.status === 'pending' && o.kind === 'create' && !!o.tableId),
      )
      setPendingHeldLoaded(true)
    })()
    return () => {
      cancelled = true
    }
  }, [offlineCapture.pendingCount])

  // Keep the receipt settings in the persisted cache. The floor used to fetch
  // them only at print time, through fetchQuery — which goes to the network
  // when stale. A tablet that had never printed online had nothing cached, so
  // an offline slip would have printed at the 80 mm default (F-FE-N72's bug)
  // or not at all.
  useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })

  useEffect(() => {
    offlineCapture.onReplayComplete((s) => {
      if (s.synced > 0) {
        // The synced tickets now exist server-side; the floor still shows the
        // pre-outage snapshot with our overlay on top. Refresh so held tiles
        // become real ones.
        invalidateTables(queryClient)
        queryClient.invalidateQueries({ queryKey: ['orders'] })
        toastHelpers.success('Back online', `${s.synced} held ticket(s) synced.`)
      }
      if (s.unreconciled > 0) {
        toastHelpers.warning(
          'Needs a manager',
          `${s.unreconciled} held ticket(s) were sent to the kitchen but could not be billed. ` +
            'A manager resolves them in Day Close.',
        )
      }
      if (s.needsFire > 0) {
        toastHelpers.warning(
          'Check the kitchen ticket',
          `${s.needsFire} synced order(s) could not be marked as already fired — ` +
            'tell the counter before anything is added to them.',
        )
      }
    })
  }, [offlineCapture, queryClient])

  const [configurator, setConfigurator] = useState<{
    product: Product
    initial: ConfiguredLine | null
    editingLineId: string | null
  } | null>(null)
  // F-DEAL-N1: memoised on data, never the clock (the sheet resets its picks
  // when `groups` changes identity).
  const configuratorProductId = configurator?.product.id ?? null
  const configuratorGroups = useMemo(
    () =>
      configuratorProductId
        ? markSoldOutDealChoices(menuModifiers.groupsForProduct(configuratorProductId), (id) => menuRules.combo_choices?.[id]?.is_available === false)
        : [],
    [configuratorProductId, menuModifiers, menuRules],
  )
  const configuratorOptionById = useMemo(
    () =>
      configuratorProductId
        ? markSoldOutDealChoiceMap(menuModifiers.optionByIdFor(configuratorProductId), (id) => menuRules.combo_choices?.[id]?.is_available === false)
        : new Map(),
    [configuratorProductId, menuModifiers, menuRules],
  )
  const configuratorIncludes = useMemo(
    () => (configuratorProductId ? (menuRules.combos?.[configuratorProductId] ?? []).map((p) => `${p.quantity} ${p.name}`) : []),
    [configuratorProductId, menuRules],
  )

  useEffect(() => {
    return () => {
      if (tapFlashTimeoutRef.current != null) clearTimeout(tapFlashTimeoutRef.current)
    }
  }, [])

  // Fresh table → clear leftover order note from a previous ticket.
  useEffect(() => {
    if (state.phase === 'guest_count' && !state.orderId) setOrderNotes('')
  }, [state.tableId, state.phase, state.orderId])

  const flashTap = useCallback((product: Product) => {
    if (tapFlashTimeoutRef.current != null) clearTimeout(tapFlashTimeoutRef.current)
    setLastTappedProductId(product.id)
    tapFlashTimeoutRef.current = setTimeout(() => {
      tapFlashTimeoutRef.current = null
      setLastTappedProductId((current) => (current === product.id ? null : current))
    }, 240)
    setTapPop({ productId: product.id, nonce: Date.now() })
  }, [])

  /**
   * Tapping a dish. Opens the option sheet only when something HAS to be
   * answered — the same rule the counter follows. A dish carrying nothing but
   * optional add-ons still rings in one tap; a waiter working a full section
   * would feel a second tap on every single item.
   */
  const addProductWithFeedback = useCallback(
    (product: Product) => {
      if (!product.is_available) return
      if (!rulesIndex.available(product)) {
        const hours = rulesIndex.hoursLabel(product)
        toastHelpers.info('Not available right now', hours ? `${product.name} is only available ${hours}.` : `${product.name} is not available right now.`)
        return
      }
      // Cold start only: until the catalogue lands we cannot tell whether this
      // dish needs a size, and adding it blind produces a line the backend
      // refuses at Fire KOT — with the waiter already at the table.
      if (menuModifiersLoading) {
        toastHelpers.info('One moment', 'Still loading this menu’s options.')
        return
      }
      if (menuModifiers.requiresConfigurator(product.id)) {
        setConfigurator({ product, initial: null, editingLineId: null })
        return
      }
      dispatch({ type: 'ADD_ITEM', product, unitPrice: dineInPrice(product) })
      flashTap(product)
    },
    [flashTap, menuModifiers, menuModifiersLoading, rulesIndex, dineInPrice],
  )

  const handleOrderNotesChange = useCallback(
    (notes: string) => {
      setOrderNotes(notes)
      if (state.orderId) {
        void apiClient.updateOrderNotes(state.orderId, notes).catch(() => {
          toastHelpers.error('Could not save order note', 'Try again before firing.')
        })
      }
    },
    [state.orderId],
  )

  // After Fire KOT: desktop browser / Electron / Server APK auto-print;
  // Android Chrome PWA opens Print modal so the tap is a user gesture for intent://.
  const printFiredKots = async (kots: StationKOT[] | undefined) => {
    // F-FE-N60: a relayed slip is already queued for the counter and needs
    // nothing from this device. Counting it here would pop the Chrome-PWA
    // "tap to print" modal for paper that is already on its way — asking a
    // waiter for a gesture that does nothing.
    const hasPrinterSlip = (kots ?? []).some((k) => !k.relayed && k.output_type === 'printer')
    if (!hasPrinterSlip) return
    if (isAndroidPrintClient() && !isBhooklyNativeServerApp()) {
      setLastFireKots(kots)
      setKotPrintOpen(true)
      return
    }
    const settingsRes = await queryClient.fetchQuery({
      queryKey: ['settings', 'all'],
      queryFn: () => apiClient.getAllSettings(),
    })
    printKotReceipts(kots, parseReceiptSettings(settingsRes.data as Record<string, unknown>))
  }

  // Compose a mode-accurate success toast from the actual KOTs the backend
  // returned, instead of always claiming "Order is on the kitchen display."
  // — which is misleading in KOT-only venues or when every station printed.
  const fireKotSuccessToast = (kots: StationKOT[] | undefined) => {
    const list = kots ?? []
    const printerCount = list.filter((k) => k.output_type === 'printer').length
    const kdsCount = list.filter((k) => k.output_type === 'kds').length
    if (kdsCount > 0 && printerCount > 0) {
      toastHelpers.success(
        'KOT sent',
        `On the kitchen display (${kdsCount}) and printing at ${printerCount} station${printerCount === 1 ? '' : 's'}.`,
      )
    } else if (kdsCount > 0) {
      toastHelpers.success('KOT sent', 'Order is on the kitchen display.')
    } else if (printerCount > 0) {
      toastHelpers.success(
        'KOT sent',
        `Printing at ${printerCount} station${printerCount === 1 ? '' : 's'}.`,
      )
    } else {
      toastHelpers.success('KOT sent', 'Order routed to the kitchen.')
    }
  }

  useEffect(() => {
    return subscribeOrderReady((e) => {
      toastHelpers.success(
        'Ready for pickup',
        `Order #${e.orderNumber} — kitchen bumped (${Math.floor(e.completionSeconds / 60)}m ${e.completionSeconds % 60}s)`
      )
      invalidateTables(queryClient)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    })
  }, [queryClient])

  // Live floor freshness: when another terminal opens/pays/clears a table, the
  // backend broadcasts on the dashboard stream. Without this, the server floor
  // kept showing a table occupied after it was paid + cleared at the counter.
  const handleTableStream = useCallback(
    (ev: DashboardStreamEvent) => {
      if (isTableAffectingEvent(ev)) invalidateTables(queryClient)
    },
    [queryClient],
  )
  const streamStatus = useDashboardStream({ onEvent: handleTableStream })

  const { data: categories = [] } = useCategories()

  const categoryById = useMemo(() => {
    const m = new Map<string, Category>()
    for (const c of categories as Category[]) m.set(c.id, c)
    return m
  }, [categories])

  const totalCategoryItems = useMemo(
    () =>
      (categories as Category[]).reduce((sum, c) => sum + (c.product_count ?? 0), 0),
    [categories],
  )

  const { data: products = [] } = useQuery({
    queryKey: ['products', selectedCategory],
    queryFn: async () => {
      if (selectedCategory === 'all') {
        // Drain all pages — /products caps at 100/page, so a single call
        // truncated the "All" menu on larger catalogues. See getAllProducts().
        return apiClient.getAllProducts()
      }
      const res = await apiClient.getProductsByCategory(selectedCategory)
      const list = res.data
      return Array.isArray(list) ? list : []
    },
  })

  /**
   * Reopen the option sheet for a staged line — how a wrongly-picked size gets
   * corrected without deleting and re-adding the dish.
   *
   * Declared here rather than with the other handlers because it closes over
   * `products` above; hoisting it would put that const in the temporal dead
   * zone when the dependency array is evaluated.
   */
  const openLineOptions = useCallback(
    (itemId: string) => {
      const line = state.items.find((i) => i.id === itemId)
      if (!line) return
      const product = products.find((p) => p.id === line.product_id)
      if (!product) return
      setConfigurator({
        product,
        initial: { selections: line.selections, quantity: line.quantity },
        editingLineId: itemId,
      })
    },
    [products, state.items],
  )

  /** Commit the option sheet — either a new line or an edit of a staged one. */
  const confirmConfigurator = useCallback(
    (line: ConfiguredLine) => {
      const ctx = configurator
      if (!ctx) return
      // Price is derived, never typed: resolveLineUnitPrice is the same
      // function the counter and the backend agree on, so a size chosen at the
      // table rings identically to one chosen at the till.
      const unitPrice = resolveLineUnitPrice(
        dineInPrice(ctx.product),
        line.selections,
        menuModifiers.priceIndexFor(ctx.product.id),
      )
      if (ctx.editingLineId) {
        dispatch({
          type: 'CONFIGURE_LINE',
          itemId: ctx.editingLineId,
          selections: line.selections,
          quantity: line.quantity,
          unitPrice,
        })
      } else {
        dispatch({ type: 'ADD_ITEM', product: ctx.product, configured: line, unitPrice })
        flashTap(ctx.product)
      }
      setConfigurator(null)
    },
    [configurator, flashTap, menuModifiers],
  )

  /** Human-readable options for a staged line ("Large", "Beef", "Extra Shot x2"). */
  const labelsForLine = useCallback(
    (selections: ReadonlyArray<LineSelection>): string[] =>
      selections
        .map((sel) => {
          // Names never vary per product (an override moves price and
          // availability only), so the shared view is the right lookup here.
          const o = menuModifiers.optionByIdFor('').get(sel.option_id)
          if (!o) return ''
          return sel.quantity > 1 ? `${o.name} x${sel.quantity}` : o.name
        })
        .filter(Boolean),
    [menuModifiers],
  )

  const { data: tables = [] } = useQuery({
    queryKey: ['tables'],
    queryFn: async () => {
      const res = await apiClient.getTables()
      return res.data || []
    },
    // Floor freshness is SSE-driven, but SSE alone is not enough: with the
    // stream down, a table freed elsewhere (counter payment, auto-release
    // sweeper) stays visibly occupied forever. 60s is the safety net; poll
    // harder while the stream is degraded.
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
  })

  // The floor as THIS tablet sees it: the server's tables with our held
  // tickets overlaid (F-FE-N125). Used for everything the waiter taps; the raw
  // `tables` stay in use where the server's word is what matters (the
  // draft-restore occupancy check, the action rail).
  const floorTables = useMemo(
    () => mergeHeldTables(tables as DiningTable[], pendingHeld),
    [tables, pendingHeld],
  )

  const { data: floorSettingRes } = useQuery({
    queryKey: ['settings', 'managed_floors'],
    queryFn: () => apiClient.getSetting('managed_floors'),
  })

  // ── Ticket persistence (F-FE-N109) ────────────────────────────────────────
  //
  // Leaving this screen for the counter unmounts it, and the whole ticket lived
  // in a reducer. On a desktop terminal nobody noticed; on the Bhookly Server
  // tablet a server crosses between the floor and the till all shift, and an
  // eight-line ticket vanished every time. The counter has had this since
  // F-FE-N31 — this is the same shape for the floor.
  const ticketUserId = useMemo(() => getDraftUserId(), [])

  const ticketFields = useMemo<TicketFields>(
    () => ({
      orderId: state.orderId,
      tableId: state.tableId,
      tableName: state.tableName,
      guestCount: state.guestCount,
      orderNotes,
      // Unsent only. A fired line already lives on the order and comes back
      // from getOrder; persisting it too would ring the round twice.
      lines: toPersistedLines(state.items, isKotUnsentStatus),
    }),
    [state.orderId, state.tableId, state.tableName, state.guestCount, state.items, orderNotes],
  )
  useTicketAutosave(ticketUserId, ticketFields, state.phase === 'ordering')

  // Is the ticket still untouched? Read at the moment a late restore lands, not
  // captured when it started — the reducer refuses a restore onto a ticket in
  // progress, but the side effects around it (order notes, the toast) have to
  // refuse too. Writing a held ticket's notes onto the table someone just
  // opened would carry them into the next KOT.
  const slatePristineRef = useRef(true)
  slatePristineRef.current = state.phase === 'table_select' && state.items.length === 0

  const ticketRestoreAttemptedRef = useRef(false)
  useEffect(() => {
    if (ticketRestoreAttemptedRef.current || !ticketUserId) return
    // Only ever restore into an untouched slate.
    if (state.phase !== 'table_select' || state.items.length > 0) return
    // F-FE-N125: a draft on a table that already has a ticket held here is
    // that ticket's next round. Wait for the queue read before deciding.
    if (!pendingHeldLoaded) return

    const saved = readTicket(ticketUserId)
    if (!saved || saved.lines.length === 0) {
      ticketRestoreAttemptedRef.current = true
      return
    }
    // Wait for what the rebuild needs: the menu for products and current
    // prices, the option catalogue to re-price the lines, and the floor to
    // judge whether the table is still ours. Returning without arming the ref
    // means we try again on the next render.
    if (products.length === 0 || menuModifiersLoading) return
    if (saved.tableId && tables.length === 0) return

    ticketRestoreAttemptedRef.current = true

    // A ticket that was never fired has no order behind it. If someone else has
    // since opened that table, restoring would let this server Fire and create
    // a SECOND order on an occupied table — the exact failure openExistingTableOrder
    // guards against. Losing a few unsent lines is much cheaper than double-billing
    // a table, so the ticket is dropped and the server is told why.
    const savedTable = saved.tableId
      ? (tables as DiningTable[]).find((t) => t.id === saved.tableId)
      : undefined
    // Raw server tables above, deliberately: a table our own held entry
    // occupies must not discard its own next round.
    const heldForTable = heldEntryForTable(pendingHeld, saved.tableId)
    if (saved.orderId == null && savedTable?.is_occupied && !heldForTable) {
      clearTicket(ticketUserId)
      toastHelpers.info(
        'Held ticket discarded',
        `${saved.tableName ?? 'That table'} was opened by someone else while your ticket was held.`,
      )
      return
    }

    let n = 0
    const seq = () => `draft-${Date.now()}-r${n++}`
    const { items: restored, dropped } = restoreTicketLines(
      saved.lines,
      products,
      menuModifiers.priceIndexFor,
      seq,
    )
    if (restored.length === 0) {
      clearTicket(ticketUserId)
      return
    }

    const announce = () =>
      toastHelpers.info(
        'Held ticket restored',
        dropped > 0
          ? `Your ticket is back — ${dropped} line${dropped > 1 ? 's' : ''} changed or removed (no longer on the menu).`
          : 'Your ticket is back.',
      )

    if (saved.orderId == null) {
      if (heldForTable) {
        // F-FE-N125: the table already has a ticket held here — the draft is
        // its next round, not a new order. Attach it, or Fire would queue a
        // second entry for the same table and both would land in the
        // reconciliation queue as a conflict.
        const held = heldEntryToKotItems(
          heldForTable,
          products,
          menuModifiers.priceIndexFor,
          menuModifiers.optionByIdFor,
        )
        dispatch({
          type: 'RESTORE_TICKET',
          orderId: null,
          tableId: saved.tableId,
          tableName: saved.tableName,
          guestCount: heldForTable.guestCount ?? saved.guestCount,
          items: [...held.items, ...restored],
        })
        setHeldOffline(heldForTable)
      } else {
        dispatch({
          type: 'RESTORE_TICKET',
          orderId: null,
          tableId: saved.tableId,
          tableName: saved.tableName,
          guestCount: saved.guestCount,
          items: restored,
        })
      }
      setOrderNotes(saved.orderNotes ?? '')
      announce()
      return
    }

    // Already fired at least once: the server is the truth for what was sent,
    // and the restored lines are appended to it. If the order cannot be loaded
    // we do NOT guess — dropping the server into ordering with a stale orderId
    // is how a round gets fired at the wrong ticket.
    void (async () => {
      try {
        const res = await apiClient.getOrder(saved.orderId as string)
        if (!res.success || !res.data) throw new Error(res.message || 'Order not found')
        // The server tapped a table while this was in flight. Their choice
        // wins; the held ticket stays held for the next empty slate.
        if (!slatePristineRef.current) return
        dispatch({
          type: 'RESTORE_TICKET',
          orderId: saved.orderId,
          tableId: saved.tableId,
          tableName: saved.tableName,
          guestCount: saved.guestCount || res.data.guest_count || 1,
          items: [...mapOrderItemsToKot(res.data.items), ...restored],
        })
        setOrderNotes(saved.orderNotes ?? '')
        announce()
      } catch {
        clearTicket(ticketUserId)
        toastHelpers.info(
          'Held ticket discarded',
          'The table order it belonged to could not be loaded — please ring it again.',
        )
      }
    })()
  }, [
    ticketUserId,
    state.phase,
    state.items.length,
    products,
    tables,
    menuModifiers,
    menuModifiersLoading,
    pendingHeld,
    pendingHeldLoaded,
  ])

  // Per-floor canvas dimensions the admin configured in Table Management. The
  // server floor map must size each floor the same way or table positions
  // won't line up with what the admin laid out.
  const { data: floorLayoutsRes } = useQuery({
    queryKey: ['settings', 'floor_layouts'],
    queryFn: () => apiClient.getSetting('floor_layouts'),
  })
  const floorLayouts = useMemo(
    () => parseFloorLayouts(floorLayoutsRes?.data),
    [floorLayoutsRes?.data],
  )

  const floorTabs = useMemo(
    () =>
      buildFloorTabs(
        floorSettingRes?.data,
        (tables as DiningTable[]).map((t) => t.location || 'Main Floor')
      ),
    [floorSettingRes?.data, tables]
  )

  useEffect(() => {
    if (floorTabs.length === 0) return
    if (!floorTabs.includes(layoutLocationFilter)) {
      setLayoutLocationFilter(floorTabs.includes('Main Floor') ? 'Main Floor' : floorTabs[0])
    }
  }, [floorTabs, layoutLocationFilter])

  // Occupied/total per floor — powers the count badges on the floor tabs.
  const floorCounts = useMemo(() => {
    const m: Record<string, { total: number; occupied: number }> = {}
    for (const t of floorTables) {
      const loc = t.location || 'Main Floor'
      if (!m[loc]) m[loc] = { total: 0, occupied: 0 }
      m[loc].total++
      if (t.has_active_order ?? t.is_occupied) m[loc].occupied++
    }
    return m
  }, [floorTables])

  // Drop the action-rail selection once the table is no longer an occupied,
  // order-bearing table (e.g. after it was settled + auto-freed elsewhere and
  // the SSE refresh landed).
  useEffect(() => {
    if (!selectedFloorTableId) return
    const t = floorTables.find((x) => x.id === selectedFloorTableId)
    const occupied = t ? (t.has_active_order ?? t.is_occupied) : false
    if (!t || !occupied || !t.current_order) {
      setSelectedFloorTableId(null)
    }
  }, [floorTables, selectedFloorTableId])

  // Open the action rail for an occupied table in the permanent right rail.
  const openTableActionRail = (table: DiningTable) => {
    setSelectedFloorTableId(table.id)
  }

  /**
   * Open a ticket held on this tablet (F-FE-N125). Its lines come back as SENT
   * — the kitchen already has them — and anything rung now appends to the same
   * entry and prints as its own round.
   */
  const openHeldTicket = (entry: OfflineOrder) => {
    const { items, dropped } = heldEntryToKotItems(
      entry,
      products,
      menuModifiers.priceIndexFor,
      menuModifiers.optionByIdFor,
    )
    const table = (tables as DiningTable[]).find((t) => t.id === entry.tableId)
    dispatch({
      type: 'RESTORE_TICKET',
      orderId: null,
      tableId: entry.tableId,
      tableName: table?.table_number ?? entry.tableId,
      guestCount: entry.guestCount ?? 1,
      items,
    })
    setHeldOffline(entry)
    setOfflineAppend(null)
    setOrderNotes('')
    setSelectedFloorTableId(null)
    if (dropped > 0) {
      toastHelpers.info(
        'Held ticket',
        `${dropped} line${dropped === 1 ? ' is' : 's are'} no longer on the menu and cannot be shown.`,
      )
    }
  }

  const refreshOrder = useCallback(async () => {
    if (!state.orderId) return
    const res = await apiClient.getOrder(state.orderId)
    if (res.success && res.data) {
      const mapped: KOTItem[] = (res.data.items || []).map((oi: OrderItem) => ({
        id: oi.id,
        product_id: oi.product_id,
        product_name: oi.product?.name || oi.product_id,
        quantity: oi.quantity,
        unit_price: oi.unit_price,
        special_instructions: oi.special_instructions || '',
        status: oi.status as ItemStatus,
        category_id: oi.product?.category_id,
        course_number: oi.course_number ?? 1,
        selections: selectionsFromOrderItem(oi),
      }))
      const localDrafts = state.items.filter(
        i => i.status === 'draft' && i.id.startsWith('draft-')
      )
      dispatch({ type: 'SYNC_ITEMS', items: [...mapped, ...localDrafts] })
    }
  }, [state.orderId, state.items])

  const handleVoidOrderSuccess = () => {
    setVoidOrderOpen(false)
    refreshOrder()
  }

  const handleVoidSuccess = (itemId: string) => {
    dispatch({ type: 'CONFIRM_VOID', itemId })
    refreshOrder()
  }

  const filteredProducts = useMemo(() => {
    const seen = new Set<string>()
    // F-FE-N161: the floor grid drops retired products for the same reason the
    // counter does — a product that is both unavailable and hidden from the
    // storefront cannot be ordered on any surface, so its tile is dead weight.
    // An 86'd item (unavailable but still on the storefront) is untouched.
    const uniq = withoutRetiredProducts(products).filter((p: Product) => {
      if (seen.has(p.id)) return false
      seen.add(p.id)
      return true
    })
    const q = searchTerm.trim().toLowerCase()
    if (!q) return uniq
    return uniq.filter((p: Product) => p.name.toLowerCase().includes(q))
  }, [products, searchTerm])

  /**
   * Take the round on this tablet and print its kitchen ticket here (F-FE-N125).
   *
   * Order of operations is the engine's: the entry is queued BEFORE the slip
   * prints, so a crash between the two leaves a queued order with no ticket
   * (recoverable) rather than a printed ticket with no record.
   */
  const handleOfflineFire = async (unsentItems: KOTItem[]) => {
    const userId = getDraftUserId()
    if (!userId) {
      toastHelpers.error('Offline ticket', 'Could not identify the signed-in user.')
      return
    }
    if (offlineCapture.capability.warning === 'kds_stations_unreachable') {
      toastHelpers.warning(
        'Kitchen display is offline',
        'Screen-only stations cannot receive this ticket — tell the kitchen those items by hand.',
      )
    }

    const result = await offlineCapture.captureOffline({
      heldOpId: heldOffline?.opId ?? null,
      targetOrderId: heldOffline ? null : state.orderId,
      existingTicket: heldOffline?.ticketRef ?? offlineAppend?.orderNumber,
      orderType: 'dine_in',
      tableId: state.tableId,
      tableNumber: state.tableName ?? '',
      serverName: offlineServerName(),
      orderTypeLabel: 'DINE IN',
      orderNote: orderNotes,
      userId,
      guestCount: state.guestCount,
      source: 'server',
      lines: unsentItems.map((i) => ({
        productId: i.product_id,
        quantity: i.quantity,
        ...(i.special_instructions ? { special_instructions: i.special_instructions } : {}),
        name: i.product_name,
        categoryId: i.category_id ?? '',
        // Ids only — the server reprices on replay (invariant #11).
        ...(i.selections.length > 0
          ? { selections: i.selections.map((s) => ({ option_id: s.option_id, quantity: s.quantity })) }
          : {}),
      })),
    })

    if (!result.ok) {
      if (result.reason === 'already_synced') {
        // The queue drained while the waiter was composing. The table is live
        // now: hand the ticket to the real order so the next Fire appends
        // through the online path instead of creating a second order.
        setHeldOffline(null)
        invalidateTables(queryClient)
        try {
          const live = await apiClient.getActiveOrderForTable(state.tableId ?? '')
          if (live.success && live.data?.id) {
            const localDrafts = state.items.filter((i) => isKotUnsentStatus(i.status))
            dispatch({
              type: 'ORDER_CREATED',
              orderId: live.data.id,
              items: [...mapOrderItemsToKot(live.data.items), ...localDrafts],
            })
            toastHelpers.info('Ticket has synced', 'This table is live now — tap Fire again to send the round.')
            return
          }
        } catch {
          /* fall through to the generic advice */
        }
        toastHelpers.warning(
          'Ticket has synced',
          'This table is live now — go back to the floor and tap it again to add the round.',
        )
        return
      }
      toastHelpers.error(
        'Offline ticket',
        'This tablet is not ready to take offline orders. Reconnect once to prepare it.',
      )
      return
    }

    try {
      // Read, never fetch: fetchQuery goes to the network when the entry is
      // stale, and the network is the thing that is gone (F-FE-N72).
      const cached = queryClient.getQueryData<APIResponse<Record<string, unknown>>>(['settings', 'all'])
      await printKotReceipts(result.slips, parseReceiptSettings(cached?.data ?? {}))
      toastHelpers.success(
        result.held ? `Added to ${result.ticket} — ticket printed` : `Ticket ${result.ticket} — printed`,
        'Held on this tablet. It syncs when the link returns.',
      )
    } catch {
      toastHelpers.warning(
        `Ticket ${result.ticket} held`,
        'The order is saved, but the kitchen ticket did not print. Tell the kitchen directly.',
      )
    }
    handleBackToFloor()
  }

  // Fire the kitchen — all unsent lines (no per-course hold on the server floor).
  const handleFireKOT = async () => {
    const unsentItems = state.items.filter(i => isKotUnsentStatus(i.status))
    if (unsentItems.length === 0) {
      toastHelpers.error('Nothing to send', 'Add items or wait for the order to sync.')
      return
    }

    // F-FE-N125: a held ticket's next round can only join the held entry, even
    // if the link came back a moment ago — it has no server id yet. Otherwise
    // the engine takes over only when the link is down and this tablet can
    // print; anything else is today's online path.
    if (heldOffline || (!online && offlineCapture.capability.ok)) {
      setFireBusy(true)
      try {
        await handleOfflineFire(unsentItems)
      } finally {
        setFireBusy(false)
      }
      return
    }
    if (!online) {
      toastHelpers.error('Offline', offlineHintForRefusal(offlineCapture.capability.reason))
      return
    }

    setFireBusy(true)
    try {
      if (!state.orderId) {
        const res = await apiClient.createServerOrder({
          // The server interface is dine-in only (enforced again server-side).
          table_id: state.tableId ?? undefined,
          order_type: 'dine_in',
          guest_count: state.guestCount,
          notes: orderNotes.trim() || undefined,
          items: unsentItems.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity,
            special_instructions: i.special_instructions || undefined,
            course_number: 1,
            // Required groups are enforced server-side; omitting these is a
            // rejected order, not a mispriced one.
            modifiers: i.selections.map(s => ({ option_id: s.option_id, quantity: s.quantity })),
          })),
        })
        if (!res.success || !res.data) {
          toastHelpers.error('Could not create order', res.message || 'Unknown error')
          return
        }
        const order = res.data
        dispatch({
          type: 'ORDER_CREATED',
          orderId: order.id,
          items: (order.items || []).map((oi: OrderItem) => ({
            id: oi.id,
            product_id: oi.product_id,
            product_name: oi.product?.name || '',
            quantity: oi.quantity,
            unit_price: oi.unit_price,
            special_instructions: oi.special_instructions || '',
            status: oi.status as ItemStatus,
            category_id: oi.product?.category_id,
            course_number: oi.course_number ?? 1,
        selections: selectionsFromOrderItem(oi),
          })),
        })
        invalidateTables(queryClient)
        const fireRes = await apiClient.fireKOT(order.id, undefined, shouldRelayCounterPrint())
        if (!fireRes.success) {
          toastHelpers.error('Fire KOT failed', fireRes.message || 'Could not send to kitchen')
          return
        }
        fireKotSuccessToast(fireRes.data?.kots)
        void printFiredKots(fireRes.data?.kots)
        void watchRelayedPrintJobs(fireRes.data?.relayed_print_jobs)
        const orderRes = await apiClient.getOrder(order.id)
        if (orderRes.success && orderRes.data) {
          dispatch({
            type: 'SYNC_ITEMS',
            items: (orderRes.data.items || []).map((oi: OrderItem) => ({
              id: oi.id,
              product_id: oi.product_id,
              product_name: oi.product?.name || '',
              quantity: oi.quantity,
              unit_price: oi.unit_price,
              special_instructions: oi.special_instructions || '',
              status: oi.status as ItemStatus,
              category_id: oi.product?.category_id,
              course_number: oi.course_number ?? 1,
        selections: selectionsFromOrderItem(oi),
            })),
          })
        }
        queryClient.invalidateQueries({ queryKey: ['newEnhancedKitchenOrders'] })
        // Back to the floor so the next table is one tap away (ticket stays open
        // at the counter until settle). Print modal is mounted on every phase.
        handleBackToFloor()
        return
      }

      const localUnsent = unsentItems.filter(i => i.id.startsWith('draft-'))
      if (localUnsent.length > 0) {
        await apiClient.addItemsToOrder(
          state.orderId,
          localUnsent.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity,
            special_instructions: i.special_instructions || undefined,
            course_number: 1,
            modifiers: i.selections.map(s => ({ option_id: s.option_id, quantity: s.quantity })),
          }))
        )
      }
      if (orderNotes.trim()) {
        await apiClient.updateOrderNotes(state.orderId, orderNotes)
      }
      const fireRes = await apiClient.fireKOT(state.orderId, undefined, shouldRelayCounterPrint())
      if (!fireRes.success) {
        toastHelpers.error('Fire KOT failed', fireRes.message || 'Could not send to kitchen')
        return
      }
      fireKotSuccessToast(fireRes.data?.kots)
      void printFiredKots(fireRes.data?.kots)
      void watchRelayedPrintJobs(fireRes.data?.relayed_print_jobs)
      await refreshOrder()
      queryClient.invalidateQueries({ queryKey: ['newEnhancedKitchenOrders'] })
      handleBackToFloor()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Request failed'
      toastHelpers.error('Fire KOT failed', msg)
    } finally {
      setFireBusy(false)
    }
  }

  // Leave the current ticket and return to the floor / order-type entry. Any
  // fired order stays open (it is settled at the counter), so a dine-in table
  // correctly remains occupied until payment — this only clears local UI state.
  const handleBackToFloor = () => {
    // Leaving the ticket is a decision, not an accident: drop the held copy so
    // the next visit starts clean (F-FE-N109).
    clearTicket(ticketUserId)
    ticketRestoreAttemptedRef.current = true
    dispatch({ type: 'RESET' })
    setOrderNotes('')
    setSelectedFloorTableId(null)
    setMobileWorkspaceTab('menu')
    setHeldOffline(null)
    setOfflineAppend(null)
  }

  // Open an existing occupied table's order into the ticket workspace.
  const openExistingTableOrder = (table: any) => {
    // F-FE-N125: offline, the rail cannot load the order, so the ticket opens
    // straight away with the cached summary. Lines rung now queue as an append
    // to this order and print their own ticket; the order's own lines are not
    // listed because this tablet does not have them.
    if (!online && table.current_order?.id) {
      const summary = table.current_order
      dispatch({ type: 'SELECT_TABLE', tableId: table.id, tableName: table.table_number })
      dispatch({ type: 'SET_GUEST_COUNT', guestCount: summary.guest_count || 1 })
      dispatch({ type: 'ORDER_CREATED', orderId: summary.id, items: [] })
      setHeldOffline(null)
      setOfflineAppend({
        orderNumber: summary.order_number,
        itemCount: typeof summary.item_count === 'number' ? summary.item_count : null,
      })
      setOrderNotes('')
      return
    }
    const order = table.current_order
    if (!order?.id) {
      // Occupied but we couldn't resolve the order — still let the server in.
      dispatch({ type: 'SELECT_TABLE', tableId: table.id, tableName: table.table_number })
      return
    }
    dispatch({ type: 'SELECT_TABLE', tableId: table.id, tableName: table.table_number })
    dispatch({ type: 'SET_GUEST_COUNT', guestCount: order.guest_count || 1 })
    ;(async () => {
      // If the order fails to load, do NOT leave the server parked in the
      // ordering phase with no orderId — firing from that state creates a
      // second order on an already-occupied table. Bounce back to the floor.
      try {
        const res = await apiClient.getOrder(order.id)
        if (res.success && res.data) {
          dispatch({ type: 'ORDER_CREATED', orderId: order.id, items: mapOrderItemsToKot(res.data.items) })
          setOrderNotes(typeof res.data.notes === 'string' ? res.data.notes : '')
          return
        }
        throw new Error(res.message || 'Order not found')
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Request failed'
        toastHelpers.error('Could not load table order', `${msg} — check the connection and tap the table again.`)
        dispatch({ type: 'RESET' })
        setSelectedFloorTableId(null)
        invalidateTables(queryClient)
      }
    })()
  }

  // Phase: order-type entry + dine-in table selection.
  if (state.phase === 'table_select') {
    const visibleTables = floorTables.filter((t: any) => (t.location || 'Main Floor') === layoutLocationFilter)
    const selectedFloorTable = selectedFloorTableId
      ? (floorTables.find((t) => t.id === selectedFloorTableId) ?? null)
      : null
    const availableTables = visibleTables.filter((t: any) => !(t.has_active_order ?? t.is_occupied))
    const isPlaced = (t: any) => Number.isFinite(Number(t.map_x)) && Number.isFinite(Number(t.map_y))
    const placedVisible = visibleTables.filter(isPlaced)
    const unplacedAvailable = availableTables.filter((t: any) => !isPlaced(t))
    // Only treat THIS floor as having a layout when it actually has placed
    // tables — otherwise the map would render blank while another floor's tables
    // satisfy a global check.
    const hasLayout = placedVisible.length > 0
    const activeFloorCounts = floorCounts[layoutLocationFilter] ?? { total: 0, occupied: 0 }
    const railSelected = !!(
      selectedFloorTable && (selectedFloorTable.has_active_order ?? selectedFloorTable.is_occupied)
    )

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-muted/30">
        {reservedGuard.dialog}
        {/* Command bar — floor status on the left, section switcher on the right */}
        <div className="shrink-0 border-b border-border bg-card px-4 sm:px-6 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold tracking-tight">Floor</h2>
              <span className="text-sm text-muted-foreground tabular-nums">
                <span className="font-semibold text-foreground">{activeFloorCounts.occupied}</span>
                /{activeFloorCounts.total} occupied
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
                  streamStatus === 'live'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
                )}
                title={streamStatus === 'live' ? 'Live floor updates' : 'Reconnecting — updates may lag'}
              >
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    streamStatus === 'live' ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse',
                  )}
                />
                {streamStatus === 'live' ? 'Live' : 'Reconnecting'}
              </span>
            </div>
            {/* Floor sections — segmented control, each with an occupancy bar */}
            {floorTabs.length > 0 && (
              <div className="no-scrollbar flex max-w-full gap-1 overflow-x-auto rounded-full bg-muted p-1 [&>*]:shrink-0">
                {floorTabs.map((loc) => {
                  const counts = floorCounts[loc]
                  const active = layoutLocationFilter === loc
                  const ratio = counts && counts.total > 0 ? counts.occupied / counts.total : 0
                  return (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => setLayoutLocationFilter(loc)}
                      className={cn(
                        'flex flex-col gap-1 rounded-full px-4 py-1.5 transition-colors',
                        active
                          ? 'bg-card font-semibold text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <span className="flex items-center gap-1.5 whitespace-nowrap text-sm leading-none">
                        {loc}
                        {counts && (
                          <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
                            {counts.occupied}/{counts.total}
                          </span>
                        )}
                      </span>
                      <span className="h-[3px] w-full overflow-hidden rounded-full bg-border">
                        <span
                          className="block h-full rounded-full bg-emerald-500 transition-all"
                          style={{ width: `${Math.round(ratio * 100)}%` }}
                        />
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Body — floor map (hero) + the action rail: a right column in
            landscape, a bottom panel on portrait tablets. */}
            <div
              className={cn(
                'relative flex flex-1 min-h-0 min-w-0 overflow-hidden',
                stackedPortrait && 'flex-col',
              )}
            >
              <div className="flex flex-1 min-h-0 min-w-0 flex-col px-4 sm:px-6 pb-6">
                {visibleTables.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    No tables configured
                  </div>
                ) : hasLayout ? (
                  <div className="flex h-full min-h-0 min-w-0 flex-col gap-2 [-webkit-overflow-scrolling:touch]">
                    {/* Legend — compact status key */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] shrink-0 text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full border border-border bg-background" />
                        Available
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        Occupied
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-sky-500" />
                        Seated · no order
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                        Pending
                      </span>
                      {reservedGuard.holds.size > 0 && (
                        <span className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full border border-amber-500 bg-amber-50 dark:bg-amber-950" />
                          Reserved
                        </span>
                      )}
                    </div>
                    {/* Floor map — the hero. Wrapper is chrome only; the shared
                        TableFloorMap is untouched so the counter/admin uses of
                        it don't regress. */}
                    <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                      <TableFloorMap
                        key={layoutLocationFilter}
                        fill
                        now={now}
                        tables={visibleTables}
                        selectedTableId={selectedFloorTableId ?? state.tableId ?? undefined}
                        floorLayout={resolveFloorDims(floorLayouts, layoutLocationFilter)}
                        showControls
                        onSelect={(table) => {
                          // A ticket held here opens directly — online or not
                          // (F-FE-N125).
                          const held = heldEntryForTable(pendingHeld, table.id)
                          if (held) {
                            openHeldTicket(held)
                            return
                          }
                          const occupied = table.has_active_order ?? table.is_occupied
                          if (occupied) {
                            // The rail needs the server; offline, go straight in.
                            if (online) openTableActionRail(table)
                            else openExistingTableOrder(table)
                          } else {
                            reservedGuard.guard(table, () =>
                              dispatch({ type: 'SELECT_TABLE', tableId: table.id, tableName: table.table_number }),
                            )
                          }
                        }}
                        holds={reservedGuard.holds}
                      />
                    </div>
                    {/* Tables missing floor-plan coordinates stay reachable */}
                    {unplacedAvailable.length > 0 && (
                      <div className="shrink-0">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
                          Not on floor plan
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {unplacedAvailable.map((table: any) => (
                            <button
                              key={table.id}
                              type="button"
                              onClick={() =>
                                reservedGuard.guard(table, () =>
                                  dispatch({ type: 'SELECT_TABLE', tableId: table.id, tableName: table.table_number }),
                                )
                              }
                              className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm transition-colors hover:border-primary hover:shadow-sm"
                            >
                              <span className="font-semibold">{table.table_number}</span>
                              <span className="ml-1.5 text-xs text-muted-foreground">{table.seating_capacity} seats</span>
                              {reservedGuard.holds.get(table.id) ? (
                                <span className="ml-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                                  {holdLabel(reservedGuard.holds.get(table.id)!)}
                                </span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Fallback when no floor layout has been drawn yet */
                  <div className="h-full overflow-y-auto">
                    {availableTables.length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4">
                        {availableTables.map((table: any) => (
                          <button
                            key={table.id}
                            type="button"
                            onClick={() =>
                              reservedGuard.guard(table, () =>
                                dispatch({ type: 'SELECT_TABLE', tableId: table.id, tableName: table.table_number }),
                              )
                            }
                            className="rounded-xl border border-border bg-card p-4 text-center transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md active:scale-[0.98]"
                          >
                            <div className="text-lg font-bold text-foreground">{table.table_number}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{table.seating_capacity} seats</div>
                            {reservedGuard.holds.get(table.id) ? (
                              <div className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                                {holdLabel(reservedGuard.holds.get(table.id)!)}
                              </div>
                            ) : null}
                            {table.location && <div className="text-xs text-muted-foreground">{table.location}</div>}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground">
                        All tables on this floor are occupied — tap a green table to continue its tab.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action rail. Landscape md+: permanent right column (selected
                  table's order + actions, or a floor summary). Portrait
                  tablets: a bottom panel — full detail sheet when a table is
                  selected, a slim occupancy strip otherwise. Below md a fixed
                  288px column would crush the floor map to a sliver, so the
                  rail becomes a slide-in overlay that only appears when an
                  occupied table is selected. */}
              {!stackedPortrait && railSelected && (
                <div
                  className="fixed inset-0 z-40 bg-black/50 md:hidden"
                  aria-hidden
                  onClick={() => setSelectedFloorTableId(null)}
                />
              )}
              <aside
                className={cn(
                  stackedPortrait
                    ? cn(
                        'flex w-full shrink-0 flex-col border-t border-border bg-card',
                        railSelected && 'max-h-[calc(var(--app-vh,100dvh)*0.45)]',
                      )
                    : cn(
                        'flex-col border-l border-border',
                        'fixed inset-y-0 right-0 z-50 flex w-[min(20rem,90vw)] bg-card shadow-xl transition-transform duration-200',
                        railSelected ? 'translate-x-0' : 'translate-x-full',
                        // min-h-0 + overflow-y-auto: if a broken browser ever
                        // sizes the shell taller than the window again, the
                        // rail's buttons stay reachable by scrolling the rail.
                        'md:static md:z-auto md:min-h-0 md:w-72 md:shrink-0 md:translate-x-0 md:overflow-y-auto md:bg-card/70 md:shadow-none md:transition-none xl:w-80',
                      ),
                )}
              >
                {railSelected && selectedFloorTable ? (
                  <ServerTableActionRail
                    table={selectedFloorTable}
                    now={now}
                    tables={tables as DiningTable[]}
                    variant={stackedPortrait ? 'sheet' : 'rail'}
                    onEditOrder={(t) => {
                      setSelectedFloorTableId(null)
                      openExistingTableOrder(t)
                    }}
                    onClose={() => setSelectedFloorTableId(null)}
                  />
                ) : stackedPortrait ? (
                  <>
                  {/* F-FE-N125: tickets held on this tablet, with why each has
                      not synced yet. Renders nothing when the queue is empty. */}
                  <div className="px-4 pt-2 sm:px-6">
                    <OfflineOrdersTray
                      refreshKey={offlineCapture.pendingCount + offlineCapture.unreconciledCount}
                      onSelect={(o) => openHeldTicket(o)}
                    />
                  </div>
                  <div className="flex items-center gap-4 px-4 py-2.5 sm:px-6">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                      <Store className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {layoutLocationFilter}
                      </div>
                      <div className="text-lg font-bold leading-tight tabular-nums text-foreground">
                        {(floorCounts[layoutLocationFilter]?.occupied ?? 0)}
                        <span className="text-sm font-medium text-muted-foreground">
                          /{floorCounts[layoutLocationFilter]?.total ?? 0}
                        </span>{' '}
                        <span className="text-xs font-normal text-muted-foreground">occupied</span>
                      </div>
                    </div>
                    <div className="h-1.5 w-28 shrink-0 overflow-hidden rounded-full bg-border">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{
                          width: `${Math.round(
                            ((floorCounts[layoutLocationFilter]?.occupied ?? 0) /
                              Math.max(1, floorCounts[layoutLocationFilter]?.total ?? 0)) *
                              100,
                          )}%`,
                        }}
                      />
                    </div>
                    <p className="ml-auto min-w-0 truncate text-sm text-muted-foreground">
                      Tap a <span className="font-medium text-emerald-600 dark:text-emerald-400">green</span> table to
                      view its order, or a free table to start one.
                    </p>
                  </div>
                  </>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                    {/* F-FE-N125: tickets held on this tablet. Empty in normal
                        service; appears during and just after an outage. */}
                    <div className="mb-4 w-full text-left">
                      <OfflineOrdersTray
                        refreshKey={offlineCapture.pendingCount + offlineCapture.unreconciledCount}
                        onSelect={(o) => openHeldTicket(o)}
                      />
                    </div>
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <Store className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {layoutLocationFilter}
                    </div>
                    <div className="mt-1 text-4xl font-bold tabular-nums text-foreground">
                      {(floorCounts[layoutLocationFilter]?.occupied ?? 0)}
                      <span className="text-lg font-medium text-muted-foreground">
                        /{floorCounts[layoutLocationFilter]?.total ?? 0}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">tables occupied</div>
                    <div className="mt-3 h-1.5 w-32 overflow-hidden rounded-full bg-border">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{
                          width: `${Math.round(
                            ((floorCounts[layoutLocationFilter]?.occupied ?? 0) /
                              Math.max(1, floorCounts[layoutLocationFilter]?.total ?? 0)) *
                              100,
                          )}%`,
                        }}
                      />
                    </div>
                    <p className="mt-4 max-w-[14rem] text-sm text-muted-foreground">
                      Tap a <span className="font-medium text-emerald-600 dark:text-emerald-400">green</span> table to
                      view its order and actions, or a free table to start a new one.
                    </p>
                  </div>
                )}
              </aside>
            </div>
      <KotPrintModal open={kotPrintOpen} onOpenChange={setKotPrintOpen} kots={lastFireKots} />
    </div>
    )
  }

  // Phase: Guest Count
  if (state.phase === 'guest_count') {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto bg-muted/30 p-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <div className="mb-6 text-center">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Table {state.tableName}
            </div>
            <h3 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">How many guests?</h3>
          </div>
          <div className="mb-4 grid grid-cols-4 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
              <button
                key={n}
                onClick={() => {
                  setGuestInput(String(n))
                  dispatch({ type: 'SET_GUEST_COUNT', guestCount: n })
                }}
                className="flex h-14 items-center justify-center rounded-xl border border-border bg-background text-xl font-semibold text-foreground transition-all hover:border-primary hover:bg-primary/5 active:scale-[0.97]"
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              min={1}
              placeholder="Or enter count..."
              value={guestInput}
              onChange={e => setGuestInput(e.target.value)}
              className="h-12 flex-1"
            />
            <Button
              className="h-12"
              onClick={() => {
                const count = parseInt(guestInput) || 1
                dispatch({ type: 'SET_GUEST_COUNT', guestCount: count })
              }}
              disabled={!guestInput || parseInt(guestInput) < 1}
            >
              Continue
            </Button>
          </div>
          <Button
            variant="ghost"
            className="mt-3 w-full"
            onClick={handleBackToFloor}
          >
            Back to floor
          </Button>
        </div>
      <KotPrintModal open={kotPrintOpen} onOpenChange={setKotPrintOpen} kots={lastFireKots} />
      </div>
    )
  }

  // Phase: Ordering (split layout — stacked tabs below lg)
  const unsentCount = state.items
    .filter((i) => isKotUnsentStatus(i.status))
    .reduce((n, i) => n + i.quantity, 0)

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-muted/30 xl:flex-row">
      <div
        role="tablist"
        aria-label="Server workspace"
        className="flex shrink-0 gap-1 border-b border-border bg-card p-1.5 xl:hidden"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mobileWorkspaceTab === 'menu'}
          className={cn(
            'min-h-10 flex-1 touch-manipulation rounded-lg text-sm font-medium transition-colors',
            mobileWorkspaceTab === 'menu'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
          onClick={() => setMobileWorkspaceTab('menu')}
        >
          Menu
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobileWorkspaceTab === 'ticket'}
          className={cn(
            'min-h-10 flex-1 touch-manipulation rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center',
            mobileWorkspaceTab === 'ticket'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
          onClick={() => setMobileWorkspaceTab('ticket')}
        >
          Ticket
          {unsentCount > 0 && (
            <Badge
              key={unsentCount}
              className="counter-tab-badge-pop ml-2 border-transparent bg-orange-500 px-1.5 py-0 text-xs font-semibold tabular-nums text-white"
              aria-label={`${unsentCount} item${unsentCount === 1 ? '' : 's'} to fire`}
            >
              {unsentCount}
            </Badge>
          )}
        </button>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col xl:min-h-0 xl:flex-row">
      {/* Left: Categories + Products */}
      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
          mobileWorkspaceTab === 'ticket' && 'hidden xl:flex'
        )}
      >
        {/* Ticket context header — table + guests, order status, and search */}
        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-card px-3 py-2.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={handleBackToFloor}
            aria-label="Back to floor"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold tracking-tight text-foreground">
                Table {state.tableName}
              </span>
              <span className="text-sm text-muted-foreground">
                · {state.guestCount} guest{state.guestCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
              heldOffline
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
                : state.orderId
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                heldOffline ? 'bg-amber-500' : state.orderId ? 'bg-emerald-500' : 'bg-amber-500',
              )}
            />
            {heldOffline
              ? `Held offline · ${heldOffline.ticketRef}`
              : state.orderId
                ? 'Order active'
                : 'Draft'}
          </span>
          {offlineAppend && (
            <span className="basis-full text-xs text-amber-800 dark:text-amber-300">
              {offlineAppend.itemCount != null
                ? `${offlineAppend.itemCount} item${offlineAppend.itemCount === 1 ? '' : 's'} were on this table before the outage. `
                : ''}
              New items print their own ticket and join order #{offlineAppend.orderNumber} when the link returns.
            </span>
          )}
          <div className="flex-1" />
          <div className="relative w-full sm:w-56 xl:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search menu..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
        </div>

        <MenuCategoryBar
          layout="row"
          categories={categories as Category[]}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
          totalCount={totalCategoryItems}
        />

        {/* Product Grid — same visual language as checkout counter (images + tap feedback). */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-3 [-webkit-overflow-scrolling:touch]">
          <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(190px,1fr))]">
            {filteredProducts.map((product: Product) => {
              const cat = product.category_id ? categoryById.get(product.category_id) : undefined
              const bg = categoryColor(cat, product.name)
              const outOfHours = product.is_available && !rulesIndex.available(product)
              const dealSoldOut = product.is_available && rulesIndex.comboSoldOut(product.id)
              const disabled = !product.is_available || outOfHours || dealSoldOut
              const tilePrice = rulesIndex.basePrice(product, 'dine_in')
              const showTapFlash = lastTappedProductId === product.id
              return (
                <button
                  key={product.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => addProductWithFeedback(product)}
                  className={cn(
                    'relative flex min-h-[108px] flex-col rounded-lg border border-border border-l-4 p-2 text-left shadow-sm select-none sm:p-2.5',
                    'transition-[transform,box-shadow,filter,outline] duration-100 [@media(prefers-reduced-motion:reduce)]:transition-none',
                    'active:scale-[0.96] active:brightness-[0.97] active:ring-2 active:ring-primary/40 active:ring-offset-2 [@media(prefers-reduced-motion:reduce)]:active:scale-100',
                    'disabled:pointer-events-none disabled:opacity-40',
                  )}
                  style={{
                    borderLeftColor: bg,
                    ...categorySurfaceStyle(bg),
                    ...(showTapFlash
                      ? {
                          outline: `2px solid ${bg}`,
                          outlineOffset: '3px',
                          zIndex: 1,
                        }
                      : {}),
                  }}
                >
                  {tapPop.productId === product.id && (
                    <span
                      key={tapPop.nonce}
                      aria-hidden
                      className="counter-product-plus-one pointer-events-none absolute -top-2 right-2 z-20 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white shadow-md"
                    >
                      +1
                    </span>
                  )}
                  <div className="flex min-h-0 flex-1 flex-col gap-1.5 sm:flex-row sm:gap-2">
                    <div className="flex shrink-0 justify-center sm:block">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="h-14 w-14 rounded-md object-cover bg-muted sm:h-20 sm:w-20"
                        />
                      ) : (
                        <ProductImagePlaceholder
                          className="h-14 w-14 sm:h-20 sm:w-20"
                          iconClassName="h-7 w-7 sm:h-10 sm:w-10"
                        />
                      )}
                    </div>
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden sm:h-20">
                      <span className="min-h-0 flex-1 overflow-hidden text-left text-sm font-semibold leading-snug text-foreground line-clamp-2 sm:line-clamp-3">
                        {product.name}
                      </span>
                      {outOfHours || dealSoldOut ? (
                        <span className="mt-auto shrink-0 self-end text-right text-xs font-medium text-muted-foreground">
                          {dealSoldOut ? 'Sold out' : `Available ${rulesIndex.hoursLabel(product)}`}
                        </span>
                      ) : (
                        <span className="mt-auto flex shrink-0 flex-wrap items-baseline justify-end gap-x-1.5 self-end text-xs font-normal tabular-nums sm:text-sm">
                          {rulesIndex.isCombo(product.id) && (
                            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Deal</span>
                          )}
                          {tilePrice.rule_id && tilePrice.price !== product.price && (
                            <span className="text-[11px] text-muted-foreground line-through">{formatCurrency(product.price)}</span>
                          )}
                          <span className="text-primary">{formatCurrency(tilePrice.price)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
          {filteredProducts.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">No products found</div>
          )}
        </div>
      </div>

      {/* Right: KOT Sidebar */}
      <div
        className={cn(
          'flex min-h-0 shrink-0 flex-col',
          mobileWorkspaceTab === 'menu' && 'hidden xl:flex xl:w-80',
          !isLgUp && mobileWorkspaceTab === 'ticket' && 'flex flex-1 w-full min-w-0'
        )}
      >
      <KOTSidebar
        items={state.items}
        tableName={state.tableName || ''}
        guestCount={state.guestCount}
        orderId={state.orderId}
        orderNotes={orderNotes}
        onOrderNotesChange={handleOrderNotesChange}
        onUpdateQty={(id, qty) => dispatch({ type: 'UPDATE_QTY', itemId: id, qty })}
        onSetItemNotes={(id, note) => dispatch({ type: 'SET_ITEM_NOTES', itemId: id, note })}
        onRemoveDraft={(id) => dispatch({ type: 'REMOVE_DRAFT', itemId: id })}
        onRequestVoid={(id, name, qty, price) => {
          // Append-only on a held ticket (owner decision, F-FE-N125): nothing
          // on this tablet can verify a manager PIN, and the entry has never
          // reached the server.
          if (heldOffline) {
            toastHelpers.info(
              'Held ticket',
              'Cancel it at the counter once the ticket has synced — a manager PIN is needed.',
            )
            return
          }
          dispatch({ type: 'REQUEST_VOID', itemId: id, itemName: name, qty, price })
        }}
        onRequestVoidOrder={() => {
          if (heldOffline) {
            toastHelpers.info(
              'Held ticket',
              'Cancel it at the counter once the ticket has synced — a manager PIN is needed.',
            )
            return
          }
          setVoidOrderOpen(true)
        }}
        onFireKOT={handleFireKOT}
        isFireLoading={fireBusy}
        labelsForLine={(item) => labelsForLine(item.selections)}
        onEditOptions={openLineOptions}
      />
      </div>
      </div>

      {/* Option sheet. Same component the counter uses, so a size chosen at the
          table and one chosen at the till are the same interaction and the same
          arithmetic. */}
      {configurator && (
        <ModifierConfigurator
          open
          productName={configurator.product.name}
          basePrice={dineInPrice(configurator.product)}
          groups={configuratorGroups}
          optionById={configuratorOptionById}
          includes={configuratorIncludes}
          priceIndex={menuModifiers.priceIndexFor(configurator.product.id)}
          formatCurrency={formatCurrency}
          accent={categoryColor(
            configurator.product.category_id ? categoryById.get(configurator.product.category_id) : undefined,
            configurator.product.name,
          )}
          initial={configurator.initial}
          onCancel={() => setConfigurator(null)}
          onConfirm={confirmConfigurator}
        />
      )}

      {/* PIN Modal */}
      {state.pinModal.open && state.pinModal.itemId && (
        <PinEntryModal
          orderId={state.orderId!}
          itemId={state.pinModal.itemId}
          itemName={state.pinModal.itemName}
          quantity={state.pinModal.qty}
          unitPrice={state.pinModal.price}
          itemStatus={state.items.find((i) => i.id === state.pinModal.itemId)?.status}
          onSuccess={(itemId) => handleVoidSuccess(itemId)}
          onClose={() => dispatch({ type: 'CLOSE_PIN_MODAL' })}
        />
      )}

      {/* Whole-order void: same modal, every sent line under one reason + PIN. */}
      {voidOrderOpen && state.orderId && (
        <PinEntryModal
          orderId={state.orderId}
          itemId=""
          itemName=""
          quantity={0}
          unitPrice={0}
          wholeOrder={{
            label: state.tableName ? `Table ${state.tableName}` : 'This order',
            lines: state.items
              .filter((i) => VOIDABLE_STATUSES.has(i.status))
              .map((i) => ({
                id: i.id,
                name: i.product_name,
                quantity: i.quantity,
                amount: i.unit_price * i.quantity,
              })),
          }}
          onSuccess={handleVoidOrderSuccess}
          onClose={() => setVoidOrderOpen(false)}
        />
      )}

      <KotPrintModal open={kotPrintOpen} onOpenChange={setKotPrintOpen} kots={lastFireKots} />

    </div>
  )
}
